import { useState } from "react";
import { Link } from "wouter";
import { ArrowRight, ChevronDown, MapPin, Phone } from "lucide-react";
import { trackEvent } from "@/hooks/use-analytics";
import { locationData, cityHref, sublocationHref } from "@/data/locations-nav";

type PanelProps = { onNavigate: () => void };

/** Desktop panel: city rail on the left, active city's areas on the right. */
export function LocationsDesktopPanel({ onNavigate }: PanelProps) {
  const [active, setActive] = useState(0);
  const loc = locationData[active];

  return (
    <div className="w-full px-4 lg:px-8 py-6">
      <div className="grid grid-cols-[280px_1fr] gap-6 rounded-2xl border border-border bg-card overflow-hidden max-h-[min(70vh,560px)]">
        <ul className="bg-secondary/60 border-r border-border p-2 overflow-y-auto scrollbar-thin" aria-label="Cities">
          {locationData.map((l, i) => {
            const on = i === active;
            return (
              <li key={l.slug}>
                <Link
                  href={cityHref(l.slug)}
                  onMouseEnter={() => setActive(i)}
                  onFocus={() => setActive(i)}
                  onKeyDown={(event) => {
                    if (event.key === "ArrowRight") {
                      event.preventDefault();
                      document.getElementById("locations-area-overview")?.focus();
                    }
                  }}
                  onClick={() => { onNavigate(); trackEvent("location_click", l.city); }}
                  data-testid={`link-nav-city-${l.slug}`}
                  aria-current={on ? "true" : undefined}
                  className={`group flex items-center justify-between gap-3 rounded-xl px-3 py-2.5 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${on ? "bg-card shadow-sm" : "hover:bg-card/70"}`}
                >
                  <span className="flex items-center gap-3 min-w-0">
                    <span className={`w-1 h-7 rounded-full transition-colors ${on ? "bg-primary" : "bg-transparent"}`} aria-hidden />
                    <span className="min-w-0">
                      <span className={`block font-display font-semibold text-[15px] leading-tight ${on ? "text-primary" : "text-foreground"}`}>{l.city}</span>
                      <span className="block text-[11px] text-muted-foreground">{l.region}</span>
                    </span>
                  </span>
                  <span className="font-mono text-[11px] tabular-nums text-muted-foreground">{l.areas.length}</span>
                </Link>
              </li>
            );
          })}
        </ul>

        <div className="py-6 pr-6 flex flex-col min-h-0">
          <div className="flex items-end justify-between gap-4 pb-4 border-b border-border">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-primary">{loc.region}</p>
              <h3 className="font-display text-2xl font-bold text-foreground">Air conditioning in {loc.city}</h3>
            </div>
            <Link
              id="locations-area-overview"
              href={cityHref(loc.slug)}
              onClick={() => { onNavigate(); trackEvent("location_click", loc.city); }}
              data-testid={`link-nav-city-overview-${loc.slug}`}
              className="shrink-0 inline-flex items-center gap-1.5 rounded-full bg-primary text-primary-foreground text-sm font-semibold px-4 py-2 hover:bg-primary/90 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              View {loc.city} <ArrowRight className="w-4 h-4" aria-hidden />
            </Link>
          </div>

          <ul
            key={loc.slug}
            className="mt-4 grid grid-cols-3 xl:grid-cols-4 gap-1 overflow-y-auto scrollbar-thin pr-1 motion-safe:animate-in motion-safe:fade-in duration-150"
            aria-label={`${loc.city} areas`}
          >
            {loc.areas.map((area) => (
              <li key={area}>
                <Link
                  href={sublocationHref(loc.slug, area)}
                  onClick={() => { onNavigate(); trackEvent("sublocation_click", `${loc.city}/${area}`); }}
                  data-testid={`link-nav-area-${loc.slug}-${area}`}
                  className="group flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-foreground/80 hover:bg-secondary hover:text-primary focus-visible:bg-secondary focus-visible:text-primary focus-visible:outline-none transition-colors"
                >
                  <MapPin className="w-3.5 h-3.5 shrink-0 text-primary/50 group-hover:text-primary" aria-hidden />
                  <span>{area}</span>
                </Link>
              </li>
            ))}
          </ul>

          <div className="mt-auto pt-4 flex items-center justify-between border-t border-border text-sm">
            <span className="text-muted-foreground">Can't see your area? Ask our team.</span>
            <a
              href="tel:02080870906"
              className="inline-flex items-center gap-2 font-semibold text-primary hover:underline"
              onClick={() => trackEvent("call_click", "Locations Menu Phone")}
              data-testid="link-nav-locations-phone"
            >
              <Phone className="w-4 h-4" aria-hidden /> 020 8087 0906
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

/** Mobile: each city is an accordion; city overview and every area are real links. */
export function LocationsMobileList({ onNavigate }: PanelProps) {
  const [openCity, setOpenCity] = useState<string | null>(null);

  return (
    <ul id="mobile-locations-menu" className="mt-2 flex flex-col gap-1.5">
      {locationData.map((l) => {
        const open = openCity === l.slug;
        const panelId = `mobile-loc-${l.slug}`;
        return (
          <li key={l.slug} className={`rounded-xl border transition-colors ${open ? "border-primary/30 bg-card" : "border-border"}`}>
            <button
              type="button"
              aria-expanded={open}
              aria-controls={panelId}
              onClick={() => setOpenCity(open ? null : l.slug)}
              data-testid={`button-mobile-city-${l.slug}`}
              className="flex w-full items-center justify-between gap-3 px-3 py-3 text-left"
            >
              <span>
                <span className={`block font-display font-semibold ${open ? "text-primary" : "text-foreground"}`}>{l.city}</span>
                <span className="block text-xs text-muted-foreground">{l.region} · {l.areas.length} areas</span>
              </span>
              <ChevronDown className={`w-4 h-4 text-muted-foreground motion-safe:transition-transform ${open ? "rotate-180" : ""}`} aria-hidden />
            </button>
            {open && (
              <div id={panelId} className="px-3 pb-3">
                <Link
                  href={cityHref(l.slug)}
                  onClick={() => { onNavigate(); trackEvent("location_click", l.city); }}
                  data-testid={`link-mobile-city-${l.slug}`}
                  className="mb-2 flex items-center justify-between rounded-lg bg-primary text-primary-foreground px-3 py-2.5 text-sm font-semibold"
                >
                  All of {l.city} <ArrowRight className="w-4 h-4" aria-hidden />
                </Link>
                <ul className="flex flex-wrap gap-1.5">
                  {l.areas.map((area) => (
                    <li key={area}>
                      <Link
                        href={sublocationHref(l.slug, area)}
                        onClick={() => { onNavigate(); trackEvent("sublocation_click", `${l.city}/${area}`); }}
                        data-testid={`link-mobile-area-${l.slug}-${area}`}
                        className="inline-flex rounded-full border border-border bg-secondary/60 px-3 py-1.5 text-xs font-medium text-foreground/80 hover:border-primary/40 hover:text-primary"
                      >
                        {area}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );
}
